import { api } from '../../shared/api/client'
import type { MarketplaceCode } from '../../shared/marketplaces'

/**
 * Схема сопоставления «поле ПИМ → характеристика маркетплейса».
 *
 * Экран настройки читает и правит таблицу `mp_characteristic_mappings` через
 * маршруты `/marketplace-mapping/*`. Кабинет не нужен: схема принадлежит
 * площадке и PIM, а не конкретному продавцу.
 *
 * `ym` заведена миграцией `20260913_ym_characteristic_mapping.sql` (Этап 2,
 * `docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §4.2) — сужаем `MarketplaceCode`
 * реестра до площадок, у которых схема сопоставления вообще есть.
 */
export type MpMarketplace = Extract<MarketplaceCode, 'wb' | 'ozon' | 'ym'>
export type MpScopeKind = 'common' | 'category'
export type MpCharacteristicFillLevel = 'category' | 'common' | 'product' | 'ignored'
export type MpValueType = 'string' | 'number'
export type MpSourceKind = 'field' | 'const' | 'category_characteristic'
export type MpEmitCondition = 'always' | 'truthy' | 'not_null' | 'positive'
export type MpTransform =
  | 'as_is'
  | 'to_string'
  | 'to_number'
  | 'to_number_or_zero'
  | 'truncate_words'
  | 'date_iso'

export interface MpMappingRule {
  id: number
  marketplace: string
  scope_kind: MpScopeKind
  /**
   * Категория площадки для переопределения: WB — «3421», OZON —
   * «17028769:96055» (category:type), ym — ID листовой категории Маркета
   * (`ym_categories.id`, одно значение — у Маркета категория не пара, как у OZON).
   */
  scope_ref: string
  target_key: string
  target_id: number | null
  target_name: string
  value_type: MpValueType
  source_kind: MpSourceKind
  source_path: string
  source_fallback_path: string | null
  transform: MpTransform
  transform_arg: string | null
  emit_condition: MpEmitCondition
  is_required: boolean
  is_enabled: boolean
  sort_order: number
  note: string | null
}

export interface MpSourceField {
  id: number
  marketplace: string
  source_path: string
  label_ru: string
  /** Откуда значение приходит в объект товара — показывается под подписью. */
  origin: string
  value_type_hint: 'string' | 'number' | 'date'
  sort_order: number
  is_active: boolean | number
}

export interface MpMappingSchemaResponse {
  marketplace: string
  scope_ref: string
  /** Что реально уйдёт на площадку с учётом переопределений выбранной категории. */
  effective: MpMappingRule[]
  /** Все строки обоих уровней — экран показывает и общие, и переопределения. */
  rows: MpMappingRule[]
  /**
   * Кто заполняет характеристику площадки. Разметка глобальна для маркетплейса:
   * «Баркод» товарный у любого предмета, поэтому уровень хранится у самой
   * характеристики, а не у пары «категория + характеристика».
   */
  levels?: MpCharacteristicLevel[]
  counts: { effective: number; common: number; category: number }
}

/** Уровень заполнения характеристики и подсказка, где живёт её источник. */
export interface MpCharacteristicLevel {
  target_id: number
  target_name: string
  fill_level: MpCharacteristicFillLevel
  note?: string | null
}

/** Тело сохранения строки схемы: id не передаётся, ключ — четвёрка полей. */
export type MpMappingRuleInput = Omit<MpMappingRule, 'id' | 'marketplace'> & {
  marketplace?: string
}

function unwrap<T>(response: any): T {
  // Гейтвей заворачивает ответ сервиса в { success, data }.
  return (response?.data?.data ?? response?.data) as T
}

export const marketplaceMappingAPI = {
  getSchema: async (
    marketplace: MpMarketplace,
    scopeRef?: string,
  ): Promise<MpMappingSchemaResponse> =>
    unwrap<MpMappingSchemaResponse>(
      await api.get(`/marketplace-mapping/${marketplace}`, {
        params: scopeRef ? { scopeRef } : undefined,
      }),
    ),

  getSources: async (marketplace: MpMarketplace): Promise<MpSourceField[]> => {
    const payload = unwrap<{ sources: MpSourceField[] }>(
      await api.get(`/marketplace-mapping/${marketplace}/sources`),
    )
    return payload?.sources ?? []
  },

  saveRule: (marketplace: MpMarketplace, rule: MpMappingRuleInput) =>
    api.post(`/marketplace-mapping/${marketplace}`, { ...rule, marketplace }),

  /**
   * Удаляется только переопределение категории: общее правило выключают
   * признаком is_enabled — иначе одним кликом сносится характеристика из всех
   * карточек площадки. Отказ приходит с бэкенда, здесь его не дублируем.
   */
  deleteRule: (id: number) => api.delete(`/marketplace-mapping/rules/${id}`),
}
